import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import {
  FileManagerComponent,
  Inject,
  NavigationPane,
  DetailsView,
  Toolbar,
  ToolbarItemsDirective,
  ToolbarItemDirective
} from '@syncfusion/ej2-react-filemanager'
import moment from 'moment'

import {
  DialogDetailsPure,
  DialogTwoActions,
  DialogYesNoHLPure,
  DialogSystemGLPure,
  DropDownBtnHLPure,
  InputHLPure,
  DialogExtractEntities
} from '../../pures'
import './syncfusion-file-manager.pure.css'
import { BusinessAdministrationValidator } from '../../../validators'
import { useDeviceType } from '../../../hooks'
import { OriginalMenuModel } from './menu-model'

/**
 * @author Edgar Escobedo
 * @created 02-09-2025
 * @description - Dialog to display a list of details.
 * @typedef {Object} Props
 * @property {Array<{ detail: string, value: string | number, line:boolean | null }> } details - Array of objects with 'detail' and 'value' keys.
 * @param {Props}
 * @returns {JSX.Element}
 */
const ChildrenDialogDetails = ({ details }) => {
  return (
    <div className='syncfusionFileManager-dialog-details'>
      <ul>
        {details.map((detail, index) => {
          if (detail.line) {
            return <hr key={index} className='syncfusionFileManager-hr' />
          } else {
            return (
              <li key={index}>
                <span className='syncfusionFileManager-labelKey'>{detail.detail}:</span>{' '}
                <span className='syncfusionFileManager-labelValue'>{detail.value}</span>
              </li>
            )
          }
        })}
      </ul>
    </div>
  )
}

/**
 * @author Marissa Vargas
 * @description - Custom implementation of Syncfusion's File Manager component - To display categories, subcategories and files by company in Digital Vault.
 * @typedef {Object} Props
 * @property {function} addNewFile - Function that redirects to the digital vault's upload page.
 * @property {Array} categoriesData - Array of categories.
 * @property {function} checkDepth - Custom function to check the depth of a folder.
 * @property {Array<{ value: string, name: string }>} companies Array of companies to display in the popup dropdown.
 * @property {string} companyId - Selected company id.
 * @property {function} createNewCategory - Function to create a new category.
 * @property {function} deleteFiles - Function to delete categories, subcategories or files.
 * @property {Array} documentsData - Array of documents.
 * @property {function} downloadFile - Function to download folders or files.
 * @property {string} fileManagerPath - String with the directory path to open the fileToChange manager.
 * @property {Array<{ dateCreated: string, dateModified: string, filterPath: string, hasChild: boolean, id: number|string, imageUrl: string, isFile: boolean, name: string, parentId: number | string, permission: Object, size: number, type: string }>} fileSystemData - Array of data to populate folders/files in the File Manager.
 * @property {Object} fileObj - Reference to the file manager's instance.
 * @property {boolean} isLoading - Boolean state if the backend request is
 * @property {string} language - Language selected by the user.
 * @property {function} moveFiles - Function to move folders or files.
 * @property {Object<{ category: { copy: boolean, create: boolean, delete: boolean, download: boolean, rename: boolean }, subcategory: { copy: boolean, create: boolean, delete: boolean, download: boolean, rename: boolean }}>} permission - Object with action permissions. Varies per user.
 * @property {function} onFileOpen - Custom function triggered when a fileToChange/folder is opened.
 * @property {function} onFileSelect - Custom function triggered when a fileToChange/folder is selected.
 * @property {function} onMenuOpen - Custom function triggered when a context menu is opened.
 * @property {function} refreshFileManager - Function to trigger fetch of file manager's data.
 * @property {function} renameFile - Function to rename a folder or file.
 * @property {function} setCompanyId - Function to change the company id.
 * @property {function} setFirstLoad - Function to update the first load state.
 * @property {function} setIsLoading - Function to update loading state.
 * @property {function} setRefresh - Function to fetch data again and refresh the component.
 * @property {boolean} showAddFileBtn - Boolean state to show/hide the add fileToChange button in the toolbar.
 * @property {function} sortComparer - Custom function to sort by date.
 * @property {string} strBtn - String that appears in the new folder button.
 * @property {function} t - Translation function.
 * @property {Array<{}>}
 * @property {function} toggleErrorRequesting - Function to activate the error dialog.
 * @property {boolean} showModalEntities - Boolean state to show/hide the modal to extract entities.
 * @property {function} handleShowModalEntities - Function to open the modal to extract entities.
 * @property {function} closeModalEntities - Function to close the modal to extract entities.
 * @property {function} saveExtractedEntities - Function to save the extracted entities.
 * @property {function} onClickCheckBox - Function to handle checkbox click events for documents to OCR.
 * @property {Array} documentsToOCR - Array of documents selected for OCR processing.
 * @property {number} totalCredits - Total credits available for OCR processing.
 * @param {Props} props
 * @returns {JSX.element}
 */
function SyncfusionFileManager({
  addNewFile,
  categoriesData,
  checkDepth,
  checkPermission,
  companies,
  companyId,
  copyFiles,
  createNewCategory,
  deleteFiles,
  documentsData,
  downloadFile,
  fileManagerPath,
  fileObj,
  fileSystemData,
  language,
  moveFiles,
  permission,
  onCreated,
  onFileOpen,
  onFileSelect,
  onMenuOpen,
  refreshFileManager,
  renameFile,
  setCompanyId,
  setIsLoading,
  setFileDetails,
  setFirstLoad,
  showAddFileBtn,
  sortComparer,
  strBtn,
  t,
  userLevelOptions,
  toggleErrorRequesting,
  showModalEntities,
  handleShowModalEntities,
  closeModalEntities,
  saveExtractedEntities,
  onClickCheckBox,
  documentsToOCR,
  totalCredits
}) {
  const validator = BusinessAdministrationValidator(t)

  const addFileBtn = useRef(null)

  const [showPopup, setShowPopup] = useState(false)

  const [showCategoryDialog, setShowCategoryDialog] = useState(false)
  const [showCategoryInputDialog, setShowCategoryInputDialog] = useState(false)
  const [categoryName, setCategoryName] = useState('')
  const [level, setLevel] = useState(1)
  const [parentId, setParentId] = useState(0)
  const [categoryNameError, setCategoryNameError] = useState(false)

  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [detailsData, setDetailsData] = useState([])

  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [levelUser, setLevelUser] = useState('')
  const [fileType, setFileType] = useState('')
  const [fileToChange, setFileToChange] = useState({})

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [filesToDelete, setFilesToDelete] = useState([])
  const menuModel = OriginalMenuModel({ t, sortComparer })
  const [sharedSelected, setSharedSelected] = useState({
    isMySharedSelected: false,
    isMyReceiptsSelected: false
  })

  const { isMobile } = useDeviceType()

  const keyLabelsMap = {
    company: [
      { key: 'name', label: t('fileManager.detailsDialog.labels.companyName') },
      {
        key: 'businessName',
        label: t('fileManager.detailsDialog.labels.businessName'),
      },
      {
        line: true
      },
      {
        key: 'totalDocuments',
        label: t('fileManager.detailsDialog.labels.totalDocuments'),
      },
      {
        key: 'totalExpedientes',
        label: t('fileManager.detailsDialog.labels.totalExpedientes'),
      },
      {
        line: true
      },
      {
        key: 'totalNotificationExpired',
        label: t('fileManager.detailsDialog.labels.totalNotificationExpired'),
      },
      {
        key: 'totalExpired',
        label: t('fileManager.detailsDialog.labels.totalExpired'),
      },
      { key: 'relation', label: t('fileManager.detailsDialog.labels.relation') },
      {
        line: true
      }
    ],
    category: [
      {
        key: 'name',
        label: t('fileManager.detailsDialog.labels.categoryName'),
      },
      {
        line: true
      },
      {
        key: 'totalDocuments',
        label: t('fileManager.detailsDialog.labels.totalDocuments'),
      },
      {
        key: 'totalExpedientes',
        label: t('fileManager.detailsDialog.labels.totalExpedientes'),
      },
      {
        line: true
      },
      {
        key: 'totalNotificationExpired',
        label: t('fileManager.detailsDialog.labels.totalNotificationExpired'),
      },
      {
        key: 'totalExpired',
        label: t('fileManager.detailsDialog.labels.totalExpired'),
      },
      { key: 'relation', label: t('fileManager.detailsDialog.labels.relation') },
      {
        line: true
      }
    ],
    document: [
      {
        key: 'name',
        label: t('fileManager.detailsDialog.labels.documentName'),
      },
      {
        key: 'userName',
        label: t('fileManager.detailsDialog.labels.owner'),
      },
      {
        line: true
      },
      {
        key: 'createDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.startDate'),
      },
      {
        key: 'validationDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.expirationDate'),
      },
      {
        line: true
      },
      {
        key: 'typeDocumentName',
        label: t('fileManager.detailsDialog.labels.typeDocument'),
      },
      {
        key: 'confidential',
        label: t('fileManager.detailsDialog.labels.confidential'),
      },
      {
        line: true
      },
      {
        key: 'description',
        label: t('fileManager.detailsDialog.labels.description'),
      },
      {
        key: 'summary',
        label: t('fileManager.detailsDialog.labels.summary.ia'),
      },
      {
        line: true
      }
    ],
    expediente: [
      { key: 'name', label: t('fileManager.detailsDialog.labels.fileName') },
      {
        key: 'updatedDateStandardComplete',
        label: t('fileManager.detailsDialog.labels.updatedDate'),
      },
      {
        line: true
      },
      {
        key: 'totalFiles',
        label: t('fileManager.detailsDialog.labels.filesIncluded'),
      },
      {
        key: 'userName',
        label: t('fileManager.detailsDialog.labels.owner'),
      },
      {
        key: 'confidential',
        label: t('fileManager.detailsDialog.labels.confidential'),
      },
      {
        line: true
      },
      {
        key: 'expired',
        label: t('fileManager.detailsDialog.labels.expired'),
      },
      {
        key: 'relation',
        label: t('fileManager.detailsDialog.labels.relation'),
      },
      {
        line: true
      }
    ],
  }

  const childrenDialog = <ChildrenDialogDetails details={detailsData} />

  function getCategoryDialogText(key) {
    const isCategory = strBtn === t('fileManager.toolbar.newCategory')
    const map = {
      saveBtn: isCategory
        ? t('fileManager.createCategory.dialog.saveBtn')
        : t('fileManager.createSubcategory.dialog.saveBtn'),
      cancelBtn: t('fileManager.createCategory.dialog.cancelBtn'),
      header: isCategory
        ? t('fileManager.createCategory.dialog.header')
        : t('fileManager.createSubcategory.dialog.header'),
      errorInputText: isCategory
        ? t('fileManager.createCategory.dialog.errorInputText')
        : t('fileManager.createSubcategory.dialog.errorInputText'),
      categoryInput: isCategory
        ? t('fileManager.createCategory.dialog.categoryInput')
        : t('fileManager.createSubcategory.dialog.categoryInput'),
      confirmHeader: isCategory
        ? t('fileManager.createCategory.dialog.confirmHeader')
        : t('fileManager.createSubcategory.dialog.confirmHeader'),
      confirmDescription: isCategory
        ? t('fileManager.createCategory.dialog.confirmDescription')
        : t('fileManager.createSubcategory.dialog.confirmDescription'),
    }
    return map[key]
  }

  /** Use of MutationObserver to keep click attached to main node of tree view */
  useEffect(() => {
    let currentHeader = null

    const handleClick = (e) => {
      setShowPopup((prev) => !prev)
    }

    const attachListener = () => {
      const header = document.querySelector('.e-fullrow')
      if (header && header !== currentHeader) {
        if (currentHeader) {
          currentHeader.removeEventListener('click', handleClick)
        }
        header.addEventListener('click', handleClick)
        currentHeader = header
      }
    }

    // Initial attach
    attachListener()

    // Observe DOM changes to re-attach if .e-fullrow is replaced
    const observer = new MutationObserver(() => {
      attachListener()
    })

    const container = document.getElementById(
      'DigitalVault_filemanager_container'
    )
    if (container) {
      observer.observe(container, { childList: true, subtree: true })
    }

    return () => {
      if (currentHeader) {
        currentHeader.removeEventListener('click', handleClick)
      }
      observer.disconnect()
    }
  }, [])

  // getDetails genera un arreglo de objetos con las etiquetas y valores a mostrar en el
  // diálogo de detalles. Se memoriza con useCallback para no recrearse salvo cuando
  // cambien las dependencias t o keyLabelsMap.
  const getDetails = useCallback(({ obj, type }) => {
    const labels = keyLabelsMap[type] || []
    return labels.map(({ key, label, line }) => {
      let value = obj && key ? obj[key] : undefined
      // Para la etiqueta confidencial devolvemos sí/no traducido
      if (label === t('fileManager.detailsDialog.labels.confidential')) {
        value = obj && obj[key] ? t('fileManager.detailsDialog.labels.confidential.yes') : t('fileManager.detailsDialog.labels.confidential.no')
      }
      return { detail: label, value, line }
    })
  }, [t, keyLabelsMap])

  // getRelation calcula la relación entre el número total de documentos/expedientes y los expirados.
  const getRelation = useCallback(({ obj }) => {
    if (!obj || obj.totalExpired === 0) return 0
    return Math.ceil(
      Number((obj.totalDocuments + obj.totalExpedientes) / obj.totalExpired)
    )
  }, [])

  // getItemDetails determina qué información mostrar en el diálogo de detalles en función
  // del tipo de elemento. Se memoriza para evitar recreaciones innecesarias y sus
  // dependencias son checkDepth, categoriesData, documentsData, getRelation y getDetails.
  const getItemDetails = useCallback((item) => {
    if (!item) return []
    if (item.type === 'folder') {
      const depth = checkDepth({ str: item.filterPath })
      // Nivel de compañía (raíz)
      if (depth === 1) {
        const base = categoriesData && categoriesData[0] ? categoriesData[0] : {}
        const obj = { ...base, relation: getRelation({ obj: base }) }
        return getDetails({ obj, type: 'company' })
      }
      // Buscar categoría específica
      const matched = (categoriesData || []).find((el) => el.id === item.id)
      const catObj = matched ? { ...matched, relation: getRelation({ obj: matched }) } : {}
      return getDetails({ obj: catObj, type: 'category' })
    }
    if (item.isFile) {
      const matched = (documentsData || []).find((el) => el.id === item.id)
      if (!matched) return []
      const rawInfoType = (matched.infoType || '').toString().toLowerCase()
      const isDocument = ['documento', 'document', 'doc'].some((v) => rawInfoType.startsWith(v))
      const isExpediente = ['expediente', 'record', 'file', 'folderfile'].some((v) => rawInfoType.startsWith(v))
      if (isDocument) return getDetails({ obj: matched, type: 'document' })
      if (isExpediente) return getDetails({ obj: matched, type: 'expediente' })
      return getDetails({ obj: matched, type: 'document' })
    }
    return []
  }, [checkDepth, categoriesData, documentsData, getRelation, getDetails])

  /** @param {import('@syncfusion/ej2-react-filemanager').FileLoadEventArgs} args */
  const onFileLoad = (args) => {
    if (
      !args.fileDetails.isFile &&
      (args.module === 'LargeIconsView' || args.module === 'DetailsView' || args.module === 'NavigationPane')
    ) {
      const element = args.element
      const depthLevel =
        checkDepth({ str: args.fileDetails.filterPath })
      element.classList.add(`custom-folder-level-${depthLevel}`)
      // Custom shared folder styling: if data item is marked shared or has key indicating shared
      try {
        const fd = args.fileDetails || {}
        const keyLc = (fd.key || '').toString()
        const isSharedFlag = !!fd.shared || ['myShared', 'shared', 'myReceipts'].includes(keyLc)
        if (isSharedFlag) {
          element.classList.add(`custom-folder-${keyLc}`)
        } else {
          element.classList.remove(`custom-folder-${keyLc}`)
        }
      } catch (_) {
        // noop
      }
    }
  }


  function getNewCategoryParams(item) {
    if (item.type === 'folder') {
      const depth = checkDepth({ str: item.filterPath })
      setLevel(depth)
      setParentId(item.id)
    }
  }

  function getFileType(item) {
    if (item.isFile) {
      setFileType(t('fileManager.renameFile.dialog.file'))
    } else {
      const depth = checkDepth({ str: item.filterPath })

      if (depth === 2) {
        setFileType(t('fileManager.renameFile.dialog.cat'))
      } else {
        setFileType(t('fileManager.renameFile.dialog.sub'))
      }
    }
  }

  function toolbarClickHandler(args) {
    const elementId = fileObj.current.element.id
    const { fileDetails = [] } = args
    const hasDetails = Array.isArray(fileDetails) && fileDetails.length > 0
    const currentPath = (fileObj.current && fileObj.current.path) || fileManagerPath || ''

    switch (args.item.id) {
      case `${elementId}_tb_newfolder`:
        if (fileDetails[0].logical) {
          toggleErrorRequesting(t('fileManager.logical.noCreateCategory'))
          return
        }

        if (strBtn === t('fileManager.toolbar.newCategory')) {
          if (!permission.category.create) {
            toggleErrorRequesting(t('fileManager.permission.category.create'))
            return
          }
        } else {
          if (!permission.subcategory.create) {
            toggleErrorRequesting(t('fileManager.permission.subcategory.create'))
            return
          }
        }
        setCategoryName('')
        setCategoryNameError(false)
        setShowCategoryInputDialog(true)
        getNewCategoryParams(hasDetails ? fileDetails[0] : { type: 'folder', filterPath: currentPath, id: 0 })
        break
      case `${elementId}_tb_refresh`:
        setFileDetails(fileDetails[0])
        refreshFileManager(true)
        break
      case `${elementId}_tb_rename`:
        if (!hasDetails) return
        if (
          !checkPermission({
            action: 'rename',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        getFileType(fileDetails[0])
        setShowRenameDialog(true)
        setFileToChange(fileDetails[0])
        setNewFileName(fileDetails[0].name)
        setLevelUser(fileDetails[0].levelUser)
        break
      case `${elementId}_tb_delete`:
        if (!hasDetails) return
        if (
          !checkPermission({
            action: 'delete',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        const enrichedFilesToDelete = fileDetails.map(file => {
          if (file.isFile && Array.isArray(documentsData)) {
            const docData = documentsData.find(doc => doc.id === file.id)
            if (docData) {
              return { ...file, ...docData }
            }
          }
          return file
        })
        setShowDeleteDialog(true)
        setFilesToDelete(enrichedFilesToDelete)
        break
      case `${elementId}_tb_details`:
        if (fileDetails.length === 0) {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails({
            type: 'folder',
            filterPath: ''
          }))
        } else {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails(fileDetails[0]))
        }
        break
      default:
        break
    }
  }

  function menuClick(args) {
    const elementId = fileObj.current.element.id
    const { fileDetails = [] } = args
    const hasDetails = Array.isArray(fileDetails) && fileDetails.length > 0
    const currentPath = (fileObj.current && fileObj.current.path) || fileManagerPath || ''

    switch (args.item.id) {
      case `${elementId}_cm_newfolder`:
        if (strBtn === t('fileManager.toolbar.newCategory')) {
          if (!permission.category.create) {
            toggleErrorRequesting(t('fileManager.permission.category.create'))
            return
          }
        } else {
          if (!permission.subcategory.create) {
            toggleErrorRequesting(t('fileManager.permission.subcategory.create'))
            return
          }
        }
        setCategoryName('')
        setCategoryNameError(false)
        setShowCategoryInputDialog(true)
        getNewCategoryParams(hasDetails ? fileDetails[0] : { type: 'folder', filterPath: currentPath, id: 0 })
        break
      case `${elementId}_cm_details`:
        if (fileDetails.length === 0) {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails({
            type: 'folder',
            filterPath: ''
          }))
        } else {
          setShowDetailsDialog(true)
          setDetailsData(getItemDetails(fileDetails[0]))
        }
        break
      case `${elementId}_cm_rename`:
        if (!hasDetails) return
        if (
          !checkPermission({
            action: 'rename',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        getFileType(fileDetails[0])
        setShowRenameDialog(true)
        setFileToChange(fileDetails[0])
        setNewFileName(fileDetails[0].name)
        setLevelUser(fileDetails[0].levelUser)
        break
      case `${elementId}_cm_delete`:
        if (!hasDetails) return
        if (
          !checkPermission({
            action: 'delete',
            type: fileDetails[0].isFile ? 'file' : 'folder',
            str: fileDetails[0].filterPath
          })
        ) {
          return
        }
        const enrichedFilesToDeleteMenu = fileDetails.map(file => {
          if (file.isFile && Array.isArray(documentsData)) {
            const docData = documentsData.find(doc => doc.id === file.id)
            if (docData) {
              return { ...file, ...docData }
            }
          }
          return file
        })
        setShowDeleteDialog(true)
        setFilesToDelete(enrichedFilesToDeleteMenu)
        break
      case `${elementId}_cm_refresh`:
        setFileDetails(fileDetails[0])
        refreshFileManager(true)
        break
      case `${elementId}_cm_analyze`:
        fileObj.current.documentSelected = (fileDetails && fileDetails.length > 0) ? fileDetails[0] : null
        handleShowModalEntities()
        break
      default:
        break
    }
  }

  // Valida el nombre de carpeta/documento. Se memoriza mediante useCallback para no crear
  // una nueva instancia en cada render. Comprueba la longitud máxima y utiliza el
  // validador de BusinessAdministrationValidator.
  const validateNameInput = useCallback((name) => {
    const MAX_NAME_LENGTH = 128
    if (!name || name.length > MAX_NAME_LENGTH) return false
    const validationError = validator.businessNameValidator(name)
    return validationError === ''
  }, [validator])

  function handleSubmitCategoryInput() {
    if (!validateNameInput(categoryName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)
    setShowCategoryInputDialog(false)
    setShowCategoryDialog(true)
  }

  function handleSubmitRenameInput() {
    if (!validateNameInput(newFileName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)

    const level = checkDepth({ str: fileToChange.filterPath }) - 1

    renameFile({
      file: {
        ...fileToChange,
        newName: newFileName.trim(),
        level
      },
      levelUser
    })

    setShowRenameDialog(false)
    setNewFileName('')
  }

  function handleCreateNewCategory({ allCompanies }) {
    createNewCategory({
      categoryName: categoryName.trim(),
      companyId: companyId,
      createAllCompanies: allCompanies,
      level: level,
      parent: parentId === 0 ? null : parentId,
      type: level === 1 ? 'Cat' : 'Sub',
    })

    setShowCategoryDialog(false)
    setCategoryName('')
    setParentId(0)
    setLevel(1)
  }

  function handleCreateNewSubcategory() {
    if (!validateNameInput(categoryName)) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)

    createNewCategory({
      categoryName: categoryName.trim(),
      companyId: companyId,
      createAllCompanies: false,
      level: level,
      parent: parentId === 0 ? null : parentId,
      type: level === 1 ? 'Cat' : 'Sub',
    })

    setShowCategoryInputDialog(false)
    setCategoryName('')
    setParentId(0)
    setLevel(1)
  }

  function handleCopyMoveFiles(args) {
    args.cancel = true
    const { itemData, targetData, isCopy } = args

    if (
      !checkPermission({
        action: 'copy',
        type: itemData[0].isFile ? 'file' : 'folder',
        str: itemData[0].filterPath
      })
    ) {
      return
    }

    const targetLevel = checkDepth({ str: targetData.filterPath }) - 1

    const files = itemData.map((f) => {
      const level = checkDepth({ str: f.filterPath }) - 1
      return { ...f, level }
    })

    if (isCopy) {
      copyFiles({
        companyId: companyId,
        files: files,
        target: { ...targetData, level: targetLevel },
      })
    } else {
      moveFiles({
        companyId: companyId,
        files: files,
        target: { ...targetData, level: targetLevel },
      })
    }
  }

  function handleSubmitDeleteFile() {
    deleteFiles({
      companyId: companyId,
      files: filesToDelete,
    })

    setShowDeleteDialog(false)
    setFilesToDelete([])
  }

  // === Build contextual content for delete confirmation ===
  function getDeleteContext() {
    const count = filesToDelete?.length || 0
    if (count === 0) {
      return {
        title: t('fileManager.deleteFile.dialog.heading') || 'Eliminar',
        message: t('fileManager.deleteFile.dialog.question') || '¿Deseas eliminar los elementos seleccionados?',
        children: null,
      }
    }

    if (count > 1) {
      const allFolders = filesToDelete.every((f) => f && f.isFile === false)
      const allFiles = filesToDelete.every((f) => f && f.isFile === true)
      const fileCount = filesToDelete.filter((f) => f && f.isFile === true).length
      const isSubcategory = (f) => {
        try {
          const depth = checkDepth && f && f.filterPath ? checkDepth({ str: f.filterPath }) : undefined
          if (typeof depth === 'number') {
            return f.isFile === false && depth !== 2
          }
        } catch (_) { }
        return f && f.isFile === false && !!f.parentId
      }
      const deletableSubcategories = filesToDelete.filter((f) => {
        if (!isSubcategory(f)) return false
        try {
          return checkPermission ? checkPermission({ action: 'delete', type: 'folder', str: f.filterPath }) : true
        } catch (_) {
          return true
        }
      })
      const subcategoryCount = deletableSubcategories.length

      if (fileCount > 0 && subcategoryCount > 0) {
        const elementsKey = subcategoryCount === 1
          ? 'syncFusion.file.manager.delete.multiply.item.elements.singular'
          : 'syncFusion.file.manager.delete.multiply.item.elements'

        const elements2Key = subcategoryCount === 1
          ? 'syncFusion.file.manager.delete.multiply.item.elements2.singular'
          : 'syncFusion.file.manager.delete.multiply.item.elements2'

        const subcategoryPart = subcategoryCount === 1
          ? t(elements2Key)
          : `${subcategoryCount} ${t(elements2Key)}`

        return {
          title: t('syncFusion.file.manager.delete.multiply.item.title'),
          message: `${t('syncFusion.file.manager.delete.multiply.item.message')} ${count} ${t(elementsKey)}${subcategoryPart}`,
          children: null,
          submitLabel: t('syncFusion.file.manager.confirmed.multiply.items'),
          isJustified: false
        }
      }

      if (allFolders) {
        return {
          title: t('syncFusion.file.manager.delete.multiply.item.title.elements'),
          message: `${t('syncFusion.file.manager.delete.multiply.item.title.elements.message')} ${count} ${t('syncFusion.file.manager.delete.multiply.item.title.elements.message.2')}`,
          children: null,
          allFolders: true,
          isJustified: false
        }
      }

      if (allFiles) {
        return {
          title: t('syncFusion.file.manager.multiply.item.title'),
          message: `${t('syncFusion.file.manager.multiply.item.message')} ${count} ${t('syncFusion.file.manager.multiply.item.elements')}`,
          children: null,
          isJustified: false,
        }
      }

      return {
        title: t('syncFusion.file.manager.multiply.item.title'),
        message: `${t('syncFusion.file.manager.multiply.item.message')} ${count} ${t('syncFusion.file.manager.multiply.item.elements')}`,
        children: null,
        isJustified: false
      }
    }

    const item = filesToDelete[0]
    // Try to locate enriched document metadata to know if it's a documento or expediente
    let matched = null
    if (item && item.id != null && Array.isArray(documentsData)) {
      matched = documentsData.find((d) => d.id === item.id) || null
    }
    const safe = (v) => (v == null ? '-' : v)
    const rawInfoType = ((matched && matched.infoType) || item.infoType || item.type || '').toString().toLowerCase()
    const isFileItem = item && item.isFile === true
    const isExpediente = isFileItem && ['expediente', 'record'].some((v) => rawInfoType.includes(v))
    const isDocument = isFileItem && !isExpediente

    const name = safe((matched && matched.name) || item.name)
    const owner = safe((matched && (matched.userName || matched.owner || matched.createdBy)) || item.userName)
    const typeDoc = safe(matched && (matched.typeDocumentName || matched.type))

    let rawFechaRegistro = (matched && (matched.createDateStandardComplete || matched.dateCreated || matched.dateModified)) ||
      item.dateCreated || item.dateModified

    let fechaRegistro = '-'
    if (rawFechaRegistro && rawFechaRegistro !== '-') {
      const momentDate = moment(rawFechaRegistro)
      if (momentDate.isValid()) {
        fechaRegistro = momentDate.format('DD/MMM/YYYY').toLowerCase().replaceAll('.', '')
      } else {
        fechaRegistro = rawFechaRegistro
      }
    }
    fechaRegistro = safe(fechaRegistro)

    // Build details content.
    // - For files (document/expediente): show Nombre, Propietario; include Tipo only for documents.
    // - For folders (category/subcategory): keep only Nombre y Fecha de registro (handled by not displaying owner/type for folders here).
    const details = (
      <div className='syncfusionFileManager-delete-details'>
        <div style={{ display: 'flex' }}><div className='syncfusionFileManager-details-modal-title'>{t('syncFusion.file.manager.name.modal.trash')}</div><div className='syncfusionFileManager-delete-details-description'>{name}</div> </div>
        {isDocument && (
          <div style={{ display: 'flex' }}><div className='syncfusionFileManager-details-modal-title'>{t('syncFusion.file.manager.type.modal.trash')}</div><div className='syncfusionFileManager-delete-details-description'>{typeDoc}</div></div>
        )}
        {isFileItem && (
          <div style={{ display: 'flex' }}><div className='syncfusionFileManager-details-modal-title'>{t('syncFusion.file.manager.property.modal.trash')}</div><div className='syncfusionFileManager-delete-details-description'>{owner}</div></div>
        )}
        <div style={{ display: 'flex' }}><div className='syncfusionFileManager-details-modal-title'>{t('syncFusion.file.manager.date.registration.trash')}</div><div className='syncfusionFileManager-delete-details-description'>{fechaRegistro}</div></div>
      </div>
    )

    // Folder-like (categories/subcategories): keep existing hard-delete flow as you have it now
    if (!isFileItem) {
      const isSubcategory = !!item.parentId
      return {
        title: isSubcategory ? t('syncFusion.file.manager.delete.subcategory.modal') : t('syncFusion.file.manager.delete.category.modal'),
        message: isSubcategory
          ? t('syncFusion.file.manager.delete.subcategory.message')
          : t('syncFusion.file.manager.delete.category.message'),
        children: details,
        allFolders: true,
      }
    }

    // File-like: decide between Documento vs Expediente (Trash flow)
    const titleDoc = t('syncFusion.file.manager.title.document')
    const messageDoc = t('syncFusion.file.manager.message.document')
    const titleExp = t('syncFusion.file.manager.title.file')
    const messageExp = t('syncFusion.file.manager.message.file')

    return {
      title: isExpediente ? titleExp : titleDoc,
      message: isExpediente ? messageExp : messageDoc,
      children: details,
    }
  }

  function handleDownloadFile(args) {
    const { data } = args

    // In "myShared" area downloads are always allowed
    if (!sharedSelected.isMySharedSelected) {
      if (
        !checkPermission({
          action: 'download',
          type: data.data[0].isFile ? 'file' : 'folder',
          str: data.data[0].filterPath
        })
      ) {
        return
      }
    }

    downloadFile({
      file: data.data[0],
      companyId: companyId
    })
  }

  function AddFileButton() {
    return (
      <div className='e-add-file-container'>
        <img
          src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_FILEMANAGER_TOOLBAR_ADDFILE}`}
          alt=''
        />
        <button ref={addFileBtn} className='e-add-file' onClick={() => addNewFile()}>
          {t('fileManager.toolbar.addFile')}
        </button>
      </div>
    )
  }

  return (
    <div className='control-section' id='DigitalVault_filemanager_container'>
      <FileManagerComponent
        beforeDownload={(args) => {
          args.cancel = true
          handleDownloadFile(args)
        }}
        beforeMove={(args) => handleCopyMoveFiles(args)}
        created={onCreated}
        id='DigitalVault_filemanager'
        showThumbnail={false}
        fileSystemData={fileSystemData}
        fileLoad={onFileLoad}
        fileOpen={onFileOpen}
        fileSelect={onFileSelect}
        locale={language}
        menuClick={menuClick}
        path={fileManagerPath}
        ref={fileObj}
        toolbarSettings={{
          items: [
            'NewFolder',
            'Move',
            'Copy',
            'Paste',
            'Delete',
            'Rename',
            'SortBy',
            'Refresh',
            'Selection',
            'View',
            'Details',
          ],
        }}
        contextMenuSettings={{
          file: [
            'Open',
            '|',
            'Copy',
            'Cut',
            '|',
            'Delete',
            'Download',
            'Rename',
            '|',
            'Analyze',
            'Details',
          ],
          folder: [
            'Open',
            '|',
            'Copy',
            'Paste',
            'Cut',
            '|',
            'Delete',
            'Download',
            'Rename',
            '|',
            'Details',
          ],
          layout: [
            'SortBy',
            'View',
            'Refresh',
            '|',
            'NewFolder',
            '|',
            'Details',
            '|',
            'SelectAll',
          ]
        }}
        detailsViewSettings={menuModel}
        toolbarClick={toolbarClickHandler}
        menuOpen={onMenuOpen}
        view={'Details'}
      >
        <Inject services={[NavigationPane, DetailsView, Toolbar]} />
        <ToolbarItemsDirective>
          <ToolbarItemDirective
            name='NewFolder'
            text={strBtn}
            tooltipText={strBtn}
          />
          <ToolbarItemDirective
            name='AddFile'
            template={AddFileButton}
            visible={showAddFileBtn}
            showAlwaysInPopup={isMobile} />
          <ToolbarItemDirective name='SortBy' />
          <ToolbarItemDirective name='Refresh' />
          <ToolbarItemDirective name='Copy' />
          <ToolbarItemDirective name='Paste' />
          <ToolbarItemDirective name='Delete' />
          <ToolbarItemDirective name='Rename' />
          <ToolbarItemDirective name='Selection' />
          <ToolbarItemDirective name='View' />
          <ToolbarItemDirective name='Details' />
          <ToolbarItemDirective name='Download' />
        </ToolbarItemsDirective >
      </FileManagerComponent >
      {showPopup && (
        <DropDownBtnHLPure
          optionsDropDown={companies}
          optionsValue={companyId}
          valueItem={'value'}
          nameOption={'name'}
          onDropDownChange={(e) => {
            if (e.target.value === companyId) return
            setCompanyId(e.target.value)
            setIsLoading(true)
            setFirstLoad(true)
          }}
          open={true}
          id={'filemanager-companies'}
          alwaysOpen={true}
        />
      )
      }
      {
        showCategoryInputDialog && (
          <DialogYesNoHLPure
            btnCancel={true}
            grid={true}
            cancel={() => setShowCategoryInputDialog(false)}
            handleCancel={() => setShowCategoryInputDialog(false)}
            handleSubmit={strBtn === t('fileManager.toolbar.newCategory')
              ? handleSubmitCategoryInput
              : handleCreateNewSubcategory
            }
            saveBtn={getCategoryDialogText('saveBtn')}
            textBtnCancel={getCategoryDialogText('cancelBtn')}
            titleDialog={getCategoryDialogText('header')}
          >
            <InputHLPure
              inputActive={true}
              errorInput={categoryNameError}
              errorInputText={getCategoryDialogText('errorInputText')}
              labelInput={getCategoryDialogText('categoryInput')}
              handleChangeInput={(e) => setCategoryName(e.target.value)}
              required
              typeInput={'text'}
              valueInput={categoryName}
            />
          </DialogYesNoHLPure>
        )
      }
      {
        showCategoryDialog && (
          <DialogTwoActions
            dialogTitle={getCategoryDialogText('confirmHeader')}
            dialogDescription={getCategoryDialogText('confirmDescription')}
            primaryAction={() =>
              handleCreateNewCategory({
                allCompanies: false,
              })
            }
            primaryBtnText={t('fileManager.createCategory.dialog.primaryBtn')}
            secondaryAction={() =>
              handleCreateNewCategory({
                allCompanies: true,
              })
            }
            secondaryBtnText={t('fileManager.createCategory.dialog.secondaryBtn')}
            setCloseDialog={() => setShowCategoryDialog(false)}
          />
        )
      }
      {
        showDetailsDialog && (
          <DialogDetailsPure
            children={childrenDialog}
            dialogTitle={t('fileManager.detailsDialog.labels.detailsTitle')}
            showBtnSecondary={true}
            setCloseDialog={() => setShowDetailsDialog(false)}
          />
        )
      }
      {
        showRenameDialog && (
          <DialogYesNoHLPure
            btnCancel={true}
            grid={true}
            cancel={() => {
              setShowRenameDialog(false)
              setCategoryNameError(false)
            }}
            handleCancel={() => {
              setShowRenameDialog(false)
              setCategoryNameError(false)
            }}
            handleSubmit={() => handleSubmitRenameInput()}
            saveBtn={t('fileManager.renameFile.dialog.saveBtn')}
            textBtnCancel={t('fileManager.renameFile.dialog.cancel')}
            titleDialog={
              t('fileManager.renameFile.dialog.heading') + ' ' + fileType
            }
          >
            <div style={{ 'marginBottom': '16px' }}>
              <InputHLPure
                inputActive={true}
                errorInput={categoryNameError}
                errorInputText={t('fileManager.renameFile.dialog.errorName')}
                labelInput={
                  t('fileManager.renameFile.dialog.label') + ' ' + fileType
                }
                handleChangeInput={(e) => setNewFileName(e.target.value)}
                required
                typeInput={'text'}
                valueInput={newFileName}
              />
            </div>
            {fileType === t('fileManager.renameFile.dialog.cat') && (
              <DropDownBtnHLPure
                labelDropDown={t("digitalVaultCategoriesAdministration.add.minimumVisualization")}
                optionsDropDown={userLevelOptions}
                optionsValue={levelUser}
                valueItem={'levelUser'}
                nameOption={'name'}
                onDropDownChange={(e) => setLevelUser(e.target.value)}
                position='top'
                id={'edit-category'}
              />
            )}
          </DialogYesNoHLPure>
        )
      }
      {
        showDeleteDialog && (() => {
          const ctx = getDeleteContext()
          return (
            <DialogSystemGLPure
              showConfirm={showDeleteDialog}
              confirmTitle={ctx.title}
              confirmMessage={ctx.message}
              confirmChildren={ctx.children}
              handleConfirmDeleteFlowClose={() => setShowDeleteDialog(false)}
              handleConfirmDeleteFlowSubmit={() => handleSubmitDeleteFile()}
              labelSubmitConfirmation={(ctx && ctx.submitLabel)
                ? ctx.submitLabel
                : (ctx && ctx.allFolders)
                  ? (t('button.delete') || 'Eliminar')
                  : t('syncFusion.file.manager.button.send.to.trash')}
              labelCancelConfirmation={t('fileManager.deleteFile.dialog.cancel')}
              isJustified={ctx.isJustified !== false}

            />
          )
        })()
      }
      {
        showModalEntities && (
          <DialogExtractEntities
            documentsToOCR={documentsToOCR}
            totalCredits={totalCredits}
            onClickCheckBox={onClickCheckBox}
            closeModalEntities={closeModalEntities}
            saveEntities={saveExtractedEntities}
            onClickSkipBtn={closeModalEntities}
          />
        )
      }
    </div >
  )
}

export default SyncfusionFileManager
